/**
 * Capability Viewer Component
 *
 * Read-only display of capabilities granted by a role.
 * Groups capabilities by category: Config, Products, Analytics, Admin.
 */

import type { Capability, SystemRoleName } from "@tatvex/types";
import { SYSTEM_ROLES } from "@tatvex/types";
import { Check, Minus } from "lucide-react";
import type { ReactElement } from "react";

// ============================================================================
// Types
// ============================================================================

interface CapabilityViewerProps {
  readonly role: string;
}

interface CapabilityGroup {
  readonly label: string;
  readonly capabilities: readonly {
    readonly key: Capability;
    readonly label: string;
  }[];
}

// ============================================================================
// Constants
// ============================================================================

const CAPABILITY_GROUPS: readonly CapabilityGroup[] = [
  {
    label: "Config",
    capabilities: [
      { key: "config:read", label: "Read" },
      { key: "config:write", label: "Write" },
      { key: "config:promote", label: "Promote" },
      { key: "config:rollback", label: "Rollback" },
    ],
  },
  {
    label: "Products",
    capabilities: [
      { key: "products:read", label: "Read" },
      { key: "products:write", label: "Write" },
      { key: "products:delete", label: "Delete" },
      { key: "products:upload", label: "Upload" },
    ],
  },
  {
    label: "Analytics",
    capabilities: [
      { key: "analytics:read", label: "Read" },
      { key: "analytics:export", label: "Export" },
    ],
  },
  {
    label: "Admin",
    capabilities: [
      { key: "admin:users", label: "Users" },
      { key: "admin:roles", label: "Roles" },
      { key: "admin:tenants", label: "Tenants" },
      { key: "admin:audit", label: "Audit" },
      { key: "admin:health", label: "Health" },
    ],
  },
] as const;

// ============================================================================
// Component
// ============================================================================

/**
 * Get capabilities for a role using SYSTEM_ROLES from @tatvex/types.
 * Returns empty array for unknown roles (fail-safe).
 */
function getCapabilitiesForRole(roleName: string): readonly Capability[] {
  if (roleName in SYSTEM_ROLES) {
    return SYSTEM_ROLES[roleName as SystemRoleName].capabilities;
  }
  return [];
}

export function CapabilityViewer({ role }: CapabilityViewerProps): ReactElement {
  const grantedCapabilities = getCapabilitiesForRole(role);

  return (
    <div className="space-y-4" data-testid="capability-viewer">
      {CAPABILITY_GROUPS.map((group) => (
        <div key={group.label}>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-muted mb-2">
            {group.label}
          </h4>
          <div className="grid grid-cols-2 gap-1">
            {group.capabilities.map((cap) => {
              const isGranted = grantedCapabilities.includes(cap.key);
              return (
                <div
                  key={cap.key}
                  className="flex items-center gap-2 text-sm py-0.5"
                  data-testid={`capability-${cap.key}`}
                >
                  {isGranted ? (
                    <Check className="h-3.5 w-3.5 text-success flex-shrink-0" />
                  ) : (
                    <Minus className="h-3.5 w-3.5 text-foreground-muted/40 flex-shrink-0" />
                  )}
                  <span className={isGranted ? "text-foreground" : "text-foreground-muted/60"}>
                    {cap.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
